/**
 * Shell-thickness availability by shell material.
 *
 * EXTRACTED, NOT AUTHORED, like its `heelRaiseRules` / `forefootRules` siblings.
 * The narrowing below was inline in InsoleDesignSection's JSX.
 *
 * This field is unusual and the shape of the return type reflects it: for
 * carbon and fibreglass the thickness is an ENUM rendered as a PillRow, but for
 * the moulded shells (PA12 / TPU / polypropylene) it is a FREE NUMBER in a
 * NumInput bounded by SHELL_THICKNESS_MIN..MAX, and for EVA the field does not
 * exist at all (EVA specifies a foam type instead).
 *
 * So `null` means "this product does not enumerate shell thickness" and a
 * membership check MUST be skipped. Returning `[]` instead would flag every
 * PA12 order in the system as illegal, which is the sort of mistake that makes
 * a safety check get switched off.
 */

/*
 * The option constants live HERE, not in InsoleDesignSection, and the section
 * re-exports them. The dependency has to point this way: this module is
 * imported by `prescriptionLegality`, which must stay free of React (and of the
 * eager `@/lib/supabase` import that reaches it through the component tree), or
 * it cannot be unit-tested with plain object fixtures. Same reason
 * `prescriptionValidation.ts` takes its type from the defaults sibling rather
 * than from PrescriptionForm.
 */

/** Fibreglass blue: two thicknesses. */
export const FIBREGLASS_BLUE_THICKNESS: readonly string[] = ['1.5mm', '2.2mm'];
/** Fibreglass white (flex): one thickness, so nothing to branch on. */
export const FIBREGLASS_WHITE_THICKNESS: readonly string[] = ['1.4mm'];
// R28 - carbon options updated per Rafael: 1.4mm + 2.1mm (was 2.0mm).
// 59 delivered lines still store the retired '2.0mm'.
export const CARBON_THICKNESS: readonly string[] = ['1.4mm', '2.1mm'];

/**
 * Enumerated shell-thickness options for a shell subtype, or `null` when the
 * product does not enumerate them.
 *
 * @param subtype           the line's solution subtype
 * @param fibreglassColor   the sibling colour choice; fibreglass white (flex)
 *                          only exists in one thickness, blue in two
 */
export function shellThicknessOptionsFor(
  subtype: string | null | undefined,
  fibreglassColor: string | null | undefined,
): readonly string[] | null {
  if (subtype === 'carbon_insole') return CARBON_THICKNESS;
  if (subtype === 'fibreglass_insole') {
    return fibreglassColor === 'white' ? FIBREGLASS_WHITE_THICKNESS : FIBREGLASS_BLUE_THICKNESS;
  }
  // PA12 / TPU / polypropylene: free numeric. EVA: no such field. Sandals: no
  // shell. None of these enumerate, so membership is not a meaningful question.
  return null;
}
